import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/utils";
import { HardDrive, Search, Loader2, Package, Info, Upload, Pencil, X, Save, CheckSquare, Square, MessageCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * iter108 — Hard Drive Manager (Post-Production sub-tab).
 *
 * A dedicated inventory view for every physical drive the studio uses to
 * store post-shoot raw data. Consolidates the previously-scattered
 * `backup_drive_1..4_drone` fields into a single audit surface:
 *   • Left grid: unique drives collected across every `post_production`
 *     row, each showing every couple whose data lives on it, in which
 *     backup slot (1st / 2nd / 3rd / Drone).
 *   • Right column: full couple-by-couple table with all 4 drive columns.
 *   • Global search box: filters BOTH panels instantly when the admin
 *     types a drive number ("58") or a couple's name.
 *
 * Fully read-only here. Drive labels are edited from the Post-Production
 * Edit Entry modal — one write surface, no duplicated forms.
 */

// The 4 physical backup slots. Order matters — determines column order
// in the couple table and the "chip label" for each drive.
const SLOT_KEYS = [
  { key: "backup_drive_1",       label: "1st Backup",       short: "1st" },
  { key: "backup_drive_2",       label: "2nd Backup",       short: "2nd" },
  { key: "backup_drive_3",       label: "3rd Backup",       short: "3rd" },
  { key: "backup_drive_4_drone", label: "4th / Drone",      short: "Drone" },
];

export default function HardDriveManager() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  // iter109 — Drive-editor modal state + Excel-import file input.
  const [editRow, setEditRow] = useState(null);      // opens the drives-only modal
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  // iter115 — Mass-assign state. `selected` is a Set of row keys (id or synth key).
  const [selected, setSelected] = useState(() => new Set());
  const [massModalOpen, setMassModalOpen] = useState(false);

  const reload = async () => {
    try {
      // iter110 — Union: PP entries + past bookings. HDM must auto-list every
      // couple whose shoot date has already passed, whether or not the admin
      // has opened a PP entry for them yet. We merge by couple_name so a PP
      // stub for a couple that also has a booking is deduped.
      const [{ data: active }, { data: archived }, { data: bookingsData }] = await Promise.all([
        api.get("/postproduction?archived=false"),
        api.get("/postproduction?archived=true"),
        api.get("/bookings"),
      ]);
      const ppRows = [...(Array.isArray(active) ? active : []), ...(Array.isArray(archived) ? archived : [])];
      // iter111 — Strict past-only filter for the HDM matrix. Shoots
      // dated today or later MUST NOT appear until the day AFTER the
      // event, so a synthetic row never surprises the team member
      // BEFORE the drive has actually been dumped. `today` here is the
      // cutoff — anything `< today` is fair game.
      const today = new Date().toISOString().slice(0, 10);
      const isPast = (d) => {
        const s = String(d || "").trim();
        return !!s && s < today;
      };
      // iter174 — Hard Drive Manager must ignore CANCELLED and WAITING
      // bookings entirely: neither the couple row, nor its drive assignment,
      // nor the empty-slot availability count should be affected by them.
      // Build a set of "inactive" booking IDs from the /bookings payload;
      // then drop matching PP rows AND skip them from the synthetic append.
      const _INACTIVE = new Set(["CANCELLED", "CANCEL", "WAITING", "INQUIRY"]);
      const inactiveBookings = (Array.isArray(bookingsData) ? bookingsData : [])
        .filter((b) => _INACTIVE.has(String(b.status || "").toUpperCase()));
      const inactiveBookingIds = new Set(
        inactiveBookings.map((b) => b.id).filter(Boolean),
      );
      // Also index by couple_name/client_name because some legacy PP rows
      // link to a booking by name only (no booking_id foreign key).
      const inactiveCoupleKeys = new Set(
        inactiveBookings
          .flatMap((b) => [b.couple_name, b.client_name])
          .map((n) => String(n || "").trim().toLowerCase())
          .filter(Boolean),
      );
      const isInactiveRow = (r) => {
        if (r.booking_id && inactiveBookingIds.has(r.booking_id)) return true;
        const key = String(r.couple_name || r.client_name || "").trim().toLowerCase();
        return !!key && inactiveCoupleKeys.has(key);
      };
      // Filter PP rows too: hide entries whose event_date is today/future.
      // Legacy PP rows with no event_date on file are treated as historical
      // (they existed before iter111 gating) and always kept visible.
      const visiblePP = ppRows
        .filter((r) => !isInactiveRow(r))
        .filter((r) => {
          const d = String(r.event_date || "").trim();
          if (!d) return true;
          return d < today;
        });
      const seen = new Set(
        visiblePP
          .map((r) => (r.couple_name || r.client_name || "").trim().toLowerCase())
          .filter(Boolean),
      );
      const bookingRows = (Array.isArray(bookingsData) ? bookingsData : [])
        // iter174 — exclude CANCELLED / WAITING before shaping into rows.
        .filter((b) => !_INACTIVE.has(String(b.status || "").toUpperCase()))
        .map((b) => ({
          eventDate: (b.day1 || b.event_date || "").trim(),
          couple: (b.couple_name || b.client_name || "").trim(),
          event: (b.event || "").trim(),
          booking_id: b.id || "",
        }))
        .filter((b) => b.couple && isPast(b.eventDate))
        .filter((b) => !seen.has(b.couple.toLowerCase()))
        .map((b) => ({
          id: null,
          _synthetic: true,
          couple_name: b.couple,
          client_name: b.couple,
          event_date: b.eventDate,
          event: b.event,
          booking_id: b.booking_id,
          backup_drive_1: "",
          backup_drive_2: "",
          backup_drive_3: "",
          backup_drive_4_drone: "",
        }));
      setRows([...visiblePP, ...bookingRows]);
    } catch (e) {
      toast.error("Could not load Post-Production data — " + (e?.message || "unknown error"));
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      await reload();
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  // iter109 — Excel/CSV bulk import handler.
  const handleImport = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/postproduction/drives/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const c = data.created || 0, u = data.updated || 0, s = data.skipped || 0;
      toast.success(`Imported: ${c} created · ${u} updated · ${s} skipped`);
      await reload();
    } catch (e) {
      toast.error("Import failed — " + (e?.response?.data?.detail || e?.message || "unknown error"));
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // iter115 — Row-key helper (id for real PP rows, synthetic key for
  // past-booking placeholders). Kept as a stable string so <Set> ops work.
  const rowKeyOf = (r) => r.id || `synth-${(r.couple_name || "").toLowerCase()}-${r.event_date || ""}`;
  const toggleSelect = (r) => {
    const k = rowKeyOf(r);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  // iter115 — Called by save flows (single + mass) to surface a "Backup
  // complete ✅" WhatsApp toast for one couple. Non-blocking: if the couple
  // has no phone / no contract token, the backend simply omits the payload.
  const offerBackupPing = (ping) => {
    if (!ping || !ping.wa_me_link) return;
    toast.success(
      `Backup complete — ping ${ping.couple_name || "couple"}?`,
      {
        description: "Opens WhatsApp with a pre-filled 'Backup complete ✅' message + tracker link.",
        action: {
          label: "Send",
          onClick: () => window.open(ping.wa_me_link, "_blank", "noopener,noreferrer"),
        },
        duration: 10000,
      },
    );
  };
  const offerBackupPings = (pings = []) => {
    if (!Array.isArray(pings)) return;
    pings.forEach((p) => offerBackupPing(p));
  };

  // Every unique drive label → list of {couple, slot, event_date}.
  const drivesIndex = useMemo(() => {
    const m = new Map(); // drive label → array
    for (const r of rows) {
      for (const s of SLOT_KEYS) {
        const label = String(r[s.key] || "").trim();
        if (!label) continue;
        if (!m.has(label)) m.set(label, []);
        m.get(label).push({
          couple: r.couple_name || "—",
          slot: s.short,
          event_date: r.event_date || "",
          pp_id: r.id,
        });
      }
    }
    return m;
  }, [rows]);

  // Apply search: match on drive label OR couple name (case-insensitive).
  const s = q.trim().toLowerCase();

  const filteredDrives = useMemo(() => {
    const entries = Array.from(drivesIndex.entries());
    if (!s) return entries;
    return entries.filter(([label, occupants]) => {
      if (String(label).toLowerCase().includes(s)) return true;
      return occupants.some((o) => String(o.couple).toLowerCase().includes(s));
    });
  }, [drivesIndex, s]);

  const filteredRows = useMemo(() => {
    const arr = !s ? rows : rows.filter((r) => {
      const haystack = [
        r.couple_name, r.event, r.coordinator,
        r.backup_drive_1, r.backup_drive_2, r.backup_drive_3, r.backup_drive_4_drone,
      ]
        .filter(Boolean)
        .map(String)
        .join(" | ")
        .toLowerCase();
      return haystack.includes(s);
    });
    // iter110 — Sort by event_date DESC so the most-recently-completed
    // shoots (the ones the studio most-likely just archived onto a drive)
    // sit at the top of the matrix.
    return [...arr].sort((a, b) => {
      const ad = String(a.event_date || "");
      const bd = String(b.event_date || "");
      if (!ad && !bd) return String(a.couple_name || "").localeCompare(String(b.couple_name || ""));
      if (!ad) return 1;
      if (!bd) return -1;
      return bd.localeCompare(ad);
    });
  }, [rows, s]);

  // Rows whose every drive slot is empty — surfaced as a nudge so Rakesh
  // can fill them in from the PP Edit Entry modal.
  const missingCount = useMemo(
    () => rows.filter((r) => SLOT_KEYS.every((s) => !String(r[s.key] || "").trim())).length,
    [rows],
  );

  // Alphanumeric-natural sort so "51,52,60,Drone 64,Kahani Final 05" reads sensibly.
  const naturalSort = (a, b) =>
    String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
  filteredDrives.sort(([a], [b]) => naturalSort(a, b));

  return (
    <div className="space-y-4" data-testid="hard-drive-manager">
      {/* Filter bar */}
      <div className="card p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" strokeWidth={1.5} />
          <input
            className="input pl-9"
            placeholder="Search drive # (e.g., 58) or couple name…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="hdm-search"
          />
        </div>
        {/* iter109 — Excel/CSV bulk import. Accepts the studio's legacy
             backup log so past Drive 50-60 / Drone 64 records populate
             the vault in a single click. */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv,.txt"
          className="hidden"
          onChange={(e) => handleImport(e.target.files?.[0])}
          data-testid="hdm-import-input"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="btn-secondary inline-flex items-center gap-1.5 text-xs px-3 py-1.5"
          disabled={importing}
          title="Import an Excel/CSV backup sheet"
          data-testid="hdm-import-btn"
        >
          {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" strokeWidth={2} />}
          Import Backup Sheet (Excel/CSV)
        </button>
        <div className="text-[11px] text-stone-500 flex items-center gap-2">
          <Package className="w-3.5 h-3.5" strokeWidth={2} />
          <span data-testid="hdm-drive-count">{drivesIndex.size}</span> unique drive{drivesIndex.size === 1 ? "" : "s"}
          <span className="mx-1 text-stone-300">·</span>
          <span data-testid="hdm-row-count">{rows.length}</span> project{rows.length === 1 ? "" : "s"}
          {missingCount > 0 && (
            <>
              <span className="mx-1 text-stone-300">·</span>
              <span className="text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 font-semibold" data-testid="hdm-missing-count">
                {missingCount} unlabelled
              </span>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="card p-8 text-center text-stone-500 flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading drive inventory…
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Left: Drive inventory grid */}
          <div className="lg:col-span-2 space-y-3">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-stone-600">
              Drive Inventory
            </div>
            {filteredDrives.length === 0 ? (
              <div className="card p-6 text-center text-sm text-stone-500 italic" data-testid="hdm-drives-empty">
                {s ? "No drive matches your search." : "No drives labelled yet — open a Post-Production entry to add drive numbers."}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="hdm-drive-grid">
                {filteredDrives.map(([label, occupants]) => (
                  <DriveCard key={label} label={label} occupants={occupants} />
                ))}
              </div>
            )}
          </div>

          {/* Right: Full couple × slot matrix */}
          <div className="lg:col-span-3 space-y-3">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-stone-600 flex items-center gap-1.5 justify-between">
              <span className="inline-flex items-center gap-1.5">
                Couple × Drive Matrix
                <Info className="w-3 h-3 text-stone-400" strokeWidth={2} />
                <span className="text-[10px] italic normal-case text-stone-500 tracking-normal font-normal">
                  Edit drive numbers from the Post-Production entry modal
                </span>
              </span>
              {/* iter115 — Mass Assign toolbar (only when at least one row picked). */}
              {selected.size > 0 && (
                <span className="inline-flex items-center gap-2 normal-case tracking-normal">
                  <span className="text-[11px] text-stone-600" data-testid="hdm-selected-count">
                    <b className="text-rose-800">{selected.size}</b> selected
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelected(new Set())}
                    className="btn-ghost text-[11px] px-2 py-1"
                    data-testid="hdm-mass-clear"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={() => setMassModalOpen(true)}
                    className="btn-primary inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1"
                    data-testid="hdm-mass-assign-btn"
                  >
                    <HardDrive className="w-3.5 h-3.5" strokeWidth={2} />
                    Assign drive to {selected.size}
                  </button>
                </span>
              )}
            </div>
            <div className="card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="table" data-testid="hdm-couple-table">
                  <thead>
                    <tr>
                      <th className="w-8 text-center">
                        {/* iter115 — Select-all toggle for the currently-filtered rows. */}
                        {(() => {
                          const allKeys = filteredRows.map(rowKeyOf);
                          const allSelected = allKeys.length > 0 && allKeys.every((k) => selected.has(k));
                          const Icon = allSelected ? CheckSquare : Square;
                          return (
                            <button
                              type="button"
                              onClick={() => setSelected((prev) => {
                                const next = new Set(prev);
                                if (allSelected) { allKeys.forEach((k) => next.delete(k)); }
                                else { allKeys.forEach((k) => next.add(k)); }
                                return next;
                              })}
                              className="p-0.5 rounded hover:bg-stone-100 text-stone-500"
                              title={allSelected ? "Deselect all" : "Select all"}
                              data-testid="hdm-select-all"
                            >
                              <Icon className="w-3.5 h-3.5" strokeWidth={2} />
                            </button>
                          );
                        })()}
                      </th>
                      <th>Event Date</th>
                      <th>Couple</th>
                      {SLOT_KEYS.map((s) => (
                        <th key={s.key} className="whitespace-nowrap">{s.label}</th>
                      ))}
                      <th className="w-12 text-right"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.length === 0 ? (
                      <tr><td colSpan={8} className="text-center text-stone-500 py-8 italic">
                        {s ? "No project matches your search." : "No Post-Production projects yet."}
                      </td></tr>
                    ) : filteredRows.map((r) => {
                      const rowKey = rowKeyOf(r);
                      const isChecked = selected.has(rowKey);
                      const RowIcon = isChecked ? CheckSquare : Square;
                      return (
                      <tr key={rowKey} data-testid={`hdm-row-${rowKey}`} className={isChecked ? "bg-rose-50/50" : ""}>
                        <td className="text-center">
                          <button
                            type="button"
                            onClick={() => toggleSelect(r)}
                            className={"p-0.5 rounded hover:bg-stone-100 " + (isChecked ? "text-rose-700" : "text-stone-400")}
                            title={isChecked ? "Deselect" : "Select for mass drive assign"}
                            data-testid={`hdm-select-${rowKey}`}
                          >
                            <RowIcon className="w-3.5 h-3.5" strokeWidth={2} />
                          </button>
                        </td>
                        <td className="font-mono text-xs">{fmtDate(r.event_date)}</td>
                        <td className="font-medium">
                          <div className="flex items-center gap-1.5">
                            <span>{r.couple_name || "—"}</span>
                            {r.event && (
                              <span className="text-[10px] font-mono text-stone-500 bg-stone-100 border border-stone-200 rounded-full px-1.5 py-0.5">
                                {r.event}
                              </span>
                            )}
                            {r._synthetic && (
                              <span
                                className="text-[9px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5"
                                title="Booking has passed but no drive labels have been logged yet"
                                data-testid={`hdm-row-synthetic-${rowKey}`}
                              >
                                NEW
                              </span>
                            )}
                          </div>
                        </td>
                        {SLOT_KEYS.map((s) => {
                          const val = String(r[s.key] || "").trim();
                          return (
                            <td key={s.key} className="font-mono text-xs">
                              {val ? (
                                <span className="inline-flex items-center gap-1 rounded bg-indigo-50 border border-indigo-200 text-indigo-800 px-1.5 py-0.5">
                                  <HardDrive className="w-3 h-3" strokeWidth={2} />
                                  {val}
                                </span>
                              ) : (
                                <span className="text-stone-300">—</span>
                              )}
                            </td>
                          );
                        })}
                        <td className="text-right">
                          <button
                            type="button"
                            onClick={() => setEditRow(r)}
                            className="p-1 rounded hover:bg-stone-100 text-stone-500 hover:text-rose-700"
                            title="Update backup drives"
                            data-testid={`hdm-edit-btn-${rowKey}`}
                          >
                            <Pencil className="w-3.5 h-3.5" strokeWidth={2} />
                          </button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* iter109 — Drives-only editor modal. ZERO financial fields — the
           4 backup slots are the only editable inputs, so coordinators can
           log data dumps without ever seeing total/paid/pending. */}
      {editRow && (
        <DrivesOnlyModal
          row={editRow}
          onClose={() => setEditRow(null)}
          onSaved={async (ping) => { setEditRow(null); if (ping) offerBackupPing(ping); await reload(); }}
        />
      )}

      {/* iter115 — Mass Assign Drive modal */}
      {massModalOpen && (
        <MassAssignModal
          selectedRows={filteredRows.filter((r) => selected.has(rowKeyOf(r)))}
          onClose={() => setMassModalOpen(false)}
          onSaved={async (pings) => {
            setMassModalOpen(false);
            setSelected(new Set());
            offerBackupPings(pings || []);
            await reload();
          }}
        />
      )}
    </div>
  );
}

function DrivesOnlyModal({ row, onClose, onSaved }) {
  const [form, setForm] = useState({
    backup_drive_1: row.backup_drive_1 || "",
    backup_drive_2: row.backup_drive_2 || "",
    backup_drive_3: row.backup_drive_3 || "",
    backup_drive_4_drone: row.backup_drive_4_drone || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      let resp;
      if (row.id) {
        // Existing PP entry — clean drives-only PUT.
        resp = await api.put(`/postproduction/${row.id}/drives`, form);
      } else {
        // iter110 — Synthetic row from a past booking. Upsert to create a
        // minimal PP stub with the shoot's event context so the vault
        // reflects the drive label immediately.
        resp = await api.post(`/postproduction/drives/upsert`, {
          couple_name: row.couple_name,
          event_date: row.event_date || "",
          event: row.event || "",
          booking_id: row.booking_id || "",
          ...form,
        });
      }
      toast.success("Backup drives updated");
      // iter115 — Forward the optional backup-complete ping payload to the
      // parent so the toast affordance can be shown right after reload.
      await onSaved(resp?.data?.backup_complete_ping);
    } catch (e) {
      toast.error("Save failed — " + (e?.response?.data?.detail || e?.message || "unknown error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="hdm-drives-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200">
          <div>
            <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Update Backup Drives</div>
            <div className="text-sm font-heading font-semibold text-stone-900 truncate max-w-[300px]" title={row.couple_name}>
              {row.couple_name || "—"}
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded hover:bg-stone-100" data-testid="hdm-drives-close">
            <X className="w-4 h-4 text-stone-600" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            {SLOT_KEYS.map((s) => (
              <div key={s.key}>
                <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-0.5 font-semibold">{s.label}</div>
                <input
                  className="input py-1.5 text-sm font-mono"
                  value={form[s.key]}
                  placeholder={s.key === "backup_drive_4_drone" ? "e.g., Drone 64" : "e.g., 58"}
                  onChange={(e) => setForm((f) => ({ ...f, [s.key]: e.target.value }))}
                  data-testid={`hdm-drives-modal-${s.key}`}
                />
              </div>
            ))}
          </div>
          <div className="text-[11px] text-stone-500 italic bg-stone-50 border border-stone-200 rounded-md px-3 py-2 flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-stone-400" strokeWidth={2} />
            Only drive locations are edited here — no financial or project data is exposed.
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-stone-200 bg-stone-50 rounded-b-2xl">
          <button type="button" onClick={onClose} className="btn-ghost text-xs px-3 py-1.5" data-testid="hdm-drives-cancel">Cancel</button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="btn-primary inline-flex items-center gap-1.5 text-xs px-3 py-1.5"
            data-testid="hdm-drives-save"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" strokeWidth={2} />}
            Save Drives
          </button>
        </div>
      </div>
    </div>
  );
}

function DriveCard({ label, occupants }) {
  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 space-y-1.5" data-testid={`hdm-drive-card-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-md bg-indigo-100 border border-indigo-200 text-indigo-800 flex items-center justify-center shrink-0">
          <HardDrive className="w-3.5 h-3.5" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-stone-900 truncate" title={label}>{label}</div>
          <div className="text-[10px] uppercase tracking-wider text-stone-500 font-mono">
            {occupants.length} project{occupants.length === 1 ? "" : "s"}
          </div>
        </div>
      </div>
      <ul className="space-y-0.5 pt-1 border-t border-indigo-200/60">
        {occupants.map((o, i) => (
          <li key={i} className="flex items-center justify-between gap-2 text-xs">
            <span className="text-stone-800 truncate" title={o.couple}>{o.couple}</span>
            <span className="text-[10px] font-mono text-indigo-700 bg-white/70 border border-indigo-200 rounded px-1.5 py-0.5 shrink-0">
              {o.slot}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * iter115 — MassAssignModal
 * Rakesh's "one gesture" affordance: pick a slot + type ONE drive label,
 * then stamp it across every selected couple. Coordinator-safe (no
 * financial fields), same permissions as the drives-only PUT endpoints.
 */
function MassAssignModal({ selectedRows, onClose, onSaved }) {
  const [slot, setSlot] = useState(SLOT_KEYS[0].key);
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const val = String(label || "").trim();
    if (!val) {
      toast.error("Type a drive number first (e.g., 58 or Drone 64).");
      return;
    }
    setSaving(true);
    try {
      const targets = selectedRows.map((r) => ({
        couple_name: r.couple_name || "",
        event_date: r.event_date || "",
        event: r.event || "",
        booking_id: r.booking_id || "",
      }));
      const { data } = await api.post(`/postproduction/drives/mass-assign`, {
        slot,
        drive_label: val,
        targets,
      });
      toast.success(`Stamped ${data.drive_label} onto ${data.count} couple${data.count === 1 ? "" : "s"}`);
      await onSaved(data.backup_complete_pings || []);
    } catch (e) {
      toast.error("Mass assign failed — " + (e?.response?.data?.detail || e?.message || "unknown error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="hdm-mass-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200">
          <div>
            <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Mass Assign Drive</div>
            <div className="text-sm font-heading font-semibold text-stone-900">
              Stamp one drive across {selectedRows.length} couple{selectedRows.length === 1 ? "" : "s"}
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded hover:bg-stone-100" data-testid="hdm-mass-close">
            <X className="w-4 h-4 text-stone-600" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1 font-semibold">Backup Slot</div>
              <select
                className="input py-1.5 text-sm"
                value={slot}
                onChange={(e) => setSlot(e.target.value)}
                data-testid="hdm-mass-slot"
              >
                {SLOT_KEYS.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1 font-semibold">Drive Label</div>
              <input
                className="input py-1.5 text-sm font-mono"
                value={label}
                placeholder={slot === "backup_drive_4_drone" ? "e.g., Drone 64" : "e.g., 58"}
                onChange={(e) => setLabel(e.target.value)}
                autoFocus
                data-testid="hdm-mass-label"
              />
            </div>
          </div>
          <div className="rounded-md border border-stone-200 bg-stone-50 max-h-40 overflow-y-auto text-xs" data-testid="hdm-mass-preview">
            {selectedRows.length === 0 ? (
              <div className="p-3 text-stone-500 italic">No couples selected.</div>
            ) : (
              <ul className="divide-y divide-stone-200">
                {selectedRows.map((r) => (
                  <li key={r.id || r.couple_name} className="px-3 py-1.5 flex items-center justify-between gap-2">
                    <span className="truncate font-medium">{r.couple_name || "—"}</span>
                    <span className="text-[10px] font-mono text-stone-500">{r.event_date || "—"}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="text-[11px] text-stone-500 italic bg-emerald-50/50 border border-emerald-200 rounded-md px-3 py-2 flex items-start gap-1.5">
            <MessageCircle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-emerald-700" strokeWidth={2} />
            Couples whose drives were previously empty will get a &quot;Backup complete&quot; WhatsApp ping (with tracker link) queued for you to send in one tap.
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-stone-200 bg-stone-50 rounded-b-2xl">
          <button type="button" onClick={onClose} className="btn-ghost text-xs px-3 py-1.5" data-testid="hdm-mass-cancel">Cancel</button>
          <button
            type="button"
            onClick={save}
            disabled={saving || selectedRows.length === 0}
            className="btn-primary inline-flex items-center gap-1.5 text-xs px-3 py-1.5"
            data-testid="hdm-mass-save"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <HardDrive className="w-3.5 h-3.5" strokeWidth={2} />}
            Stamp {label ? `"${label}"` : "drive"} on {selectedRows.length}
          </button>
        </div>
      </div>
    </div>
  );
}

